import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  type FqsRequestOptions,
  fqsRequestAll,
  MAX_OBJECTS_PER_REQUEST,
} from '@ctw/shared/api/fqs/client';
import {
  type DiagnosticReportGraphqlResponse,
  getDiagnosticReportOverviewQuery,
  getDiagnosticReportQuery,
} from '@ctw/shared/api/fqs/queries/diagnostic-reports-query';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS,
  QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_OVERVIEW,
} from '@ctw/shared/utils/query-keys';
import type { GraphQLClient } from 'graphql-request';
import { useMemo } from 'react';
import { SYSTEM_SUMMARY } from './system-urls';

// Gets diagnostic reports for the patient. Uses a lightweight GraphQL fragment
// that omits the Practitioner and Patient/Organization resources referenced via
// `performer.resource` and `subject.resource`. Those are only needed by the
// drawer — the drawer calls {@link useDiagnosticReportById} to refetch with the
// full fragment when it opens.
export function usePatientDiagnosticReports(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  const { requestContext } = useCTW();
  const { builderId } = requestContext;
  const shouldUseFqsDiagnosticReportHotstorage = useFeatureFlag(
    'ctw-fqs-use-diagnostic-report-hotstorage',
  );

  const options = useMemo(
    () => ({
      staleTime: 30_000,
      queryId: QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS_OVERVIEW,
      queryKey: [limit], // Only use the IDs in our key (fixes issue with circular references).
      queryFn: async ({
        graphqlClient,
        telemetry,
        patient,
      }: {
        graphqlClient: GraphQLClient;
        telemetry: ReturnType<typeof useTelemetry>;
        patient: PatientModel;
      }) => {
        const data = await fetchDiagnosticReportsOverviewFromFQS(
          telemetry,
          graphqlClient,
          patient,
          limit,
          {
            requestHeaders: {
              'X-Use-Hotstorage': shouldUseFqsDiagnosticReportHotstorage ? '1' : '0',
            },
          },
        );

        // Precompute the sort flag and dedupe key for each report once. The
        // previous implementation used `uniqWith(..., isEqual)` which is O(n²)
        // and reinvoked the `displayName` / `dateDisplay` / `ownedByBuilder`
        // getters millions of times for large result sets — blocking the main
        // thread and keeping `isLoading` true after the network was done.
        const enrichedReports = data.DiagnosticReportConnection.edges.map((x) => {
          const report = new DiagnosticReportModel(x.node, undefined, x.node.BasicList);
          return {
            report,
            isOwned: report.ownedByBuilder(builderId),
            dedupeKey: `${report.displayName}\u0000${report.dateDisplay ?? ''}`,
          };
        });

        // Stable-sort (Array.prototype.sort is stable in all modern engines)
        // with builder-owned reports first.
        enrichedReports.sort((a, b) => {
          if (a.isOwned === b.isOwned) {
            return 0;
          }
          return a.isOwned ? -1 : 1;
        });

        // Dedupe via Set in O(n), preserving first occurrence (builder-owned wins).
        const seen = new Set<string>();
        const deduped: Array<DiagnosticReportModel> = [];
        for (const { report, dedupeKey } of enrichedReports) {
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            deduped.push(report);
          }
        }
        return deduped;
      },
      enabled,
    }),
    [builderId, limit, enabled, shouldUseFqsDiagnosticReportHotstorage],
  );

  return usePatientQuery(options);
}

/**
 * Fetches a single diagnostic report by ID with the full GraphQL fragment
 * (including performer.resource and subject.resource). Pair with
 * {@link usePatientDiagnosticReports}: the list uses the lite query for fast
 * paginated rendering; the drawer calls this hook to load the heavier
 * per-record data on demand.
 */
export function useDiagnosticReportById(id: string | undefined) {
  const shouldUseFqsDiagnosticReportHotstorage = useFeatureFlag(
    'ctw-fqs-use-diagnostic-report-hotstorage',
  );

  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_DIAGNOSTIC_REPORTS,
    queryKey: [id],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      if (!id) {
        return undefined;
      }
      // Delegate to `fetchDiagnosticReportsFromFQSById` so we share its
      // `fqsRequestAll` dedupe key with the drawer-context's deep-link fetch
      // (which also calls this function). Without shared key, the drawer would
      // fire two separate network calls for the same DR on open.
      const models = await fetchDiagnosticReportsFromFQSById(
        telemetry,
        graphqlClient,
        patient,
        [id],
        {
          requestHeaders: {
            'X-Use-Hotstorage': shouldUseFqsDiagnosticReportHotstorage ? '1' : '0',
          },
        },
      );
      return models[0];
    },
    enabled: !!id,
  });
}

async function fetchDiagnosticReportsOverviewFromFQS(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  limit: number,
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<DiagnosticReportGraphqlResponse>(
    telemetry,
    graphqlClient,
    getDiagnosticReportOverviewQuery,
    'DiagnosticReportConnection',
    {
      upid: patient.UPID,
      cursor: 'DiagnosticReportConnection',
      filter: {
        tag: { nonematch: [SYSTEM_SUMMARY] },
      },
      first: limit,
    },
    undefined,
    options,
  );
  return data;
}

export async function fetchDiagnosticReportsFromFQSById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string> = [],
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<DiagnosticReportGraphqlResponse>(
    telemetry,
    graphqlClient,
    getDiagnosticReportQuery,
    'DiagnosticReportConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: 500,
      // The `tag: { nonematch: [SYSTEM_SUMMARY] }` filter is deliberately omitted here.
      // Matches `useDiagnosticReportById` so both call sites produce the same
      // `fqsRequestAll` dedupe key and the drawer-open flow only fires one network
      // request (when the drawer body and the drawer-context's deep-link useEffect
      // both try to refetch the same DR by id).
      filter: {
        ids: { anymatch: ids },
      },
    },
    undefined,
    options,
  );
  return data.DiagnosticReportConnection.edges.map(
    (x) => new DiagnosticReportModel(x.node, undefined, x.node.BasicList),
  );
}
